import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import FarmFeaturesStyle from "../../../assets/stylesheets/farm-detail/sections/FarmFeatures.module.css";
import FarmDetailStyle from "../../../assets/stylesheets/farm-detail/FarmDetailPage.module.css";
import { ReactComponent as Close } from '../../../assets/icons/close.svg';
import { ChevronDown } from "lucide-react";

const FARM_FEATURES_TITLE = "Things you’ll love about the farm";
const FARM_FEATURES_TITLE_MOBILE = "Things you’ll love about the farm";
const VIEW_ALL_FEATURES_BUTTON_TEXT = "View all features";

export default function FarmFeatures({ features, categories }) {
  const [showModal, setShowModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimeout = useRef();

  // Disable page scroll when modal is open
  useEffect(() => {
    // Helper to get scrollbar width
    const getScrollbarWidth = () => {
      return window.innerWidth - document.documentElement.clientWidth;
    };
    if (showModal) {
      const scrollbarWidth = getScrollbarWidth();
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        // Also apply to header
        // const header = document.querySelector('header');
        // if (header) header.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open');
      document.documentElement.style.removeProperty('--scrollbar-width');
      // Remove from header
      // const header = document.querySelector('header');
      // if (header) header.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open');
      document.documentElement.style.removeProperty('--scrollbar-width');
      // Remove from header
      // const header = document.querySelector('header');
      // if (header) header.style.paddingRight = '';
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, [showModal]);

  // Modal close handler with animation
  const handleCloseModal = () => {
    setClosing(true);
    closeTimeout.current = setTimeout(() => {
      setShowModal(false);
      setClosing(false);
    }, 200);
  };

  if (!features || features.length === 0) return null;

  // Show only first 6 features in the section, rest in modal
  const previewCount = 6;
  const previewFeatures = features.slice(0, previewCount);
  const hasMore = features.length > previewCount;

  // Helper: detect mobile
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 787px)').matches;

  return (
    <section className={FarmFeaturesStyle["feature-section"]}>
      <div className={FarmFeaturesStyle["feature-list-container"]}>
        <h3 className={FarmDetailStyle["farmDetail-title"]}>{FARM_FEATURES_TITLE}</h3>
        <ul className={FarmFeaturesStyle["feature-list"]}>
          {previewFeatures.map(f => (
            <li key={f.id} className={FarmFeaturesStyle["feature-item"]}>
              <span className={FarmFeaturesStyle["feature-icon"]} dangerouslySetInnerHTML={{ __html: f.iconSvg }} />
              <span className={FarmFeaturesStyle["feature-label"]}>{f.name}</span>
            </li>
          ))}
        </ul>
        {hasMore && (
          <div className={FarmFeaturesStyle["feature-viewall-btn"]}>
            <button
              className={FarmFeaturesStyle["feature-viewall-btn"]}
              type="button"
              onClick={() => setShowModal(true)}
            >
              {VIEW_ALL_FEATURES_BUTTON_TEXT}
            </button>
          </div>
        )}
      </div>

      {/* Desktop modal */}
      {showModal && !isMobile && (
        <div className={FarmFeaturesStyle["feature-modal-backdrop"]} onClick={handleCloseModal}>
          <div
            className={FarmFeaturesStyle["feature-modal"] + (closing ? ' ' + FarmFeaturesStyle["closing"] : '')}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onClick={e => e.stopPropagation()}
          >
            <div>
              <div className={FarmFeaturesStyle["feature-modal-header"]}>
                <span className={FarmFeaturesStyle["feature-modal-title"]}>
                  {FARM_FEATURES_TITLE}
                </span>
                <button
                  className={FarmFeaturesStyle["feature-modal-close"]}
                  aria-label="Close"
                  onClick={handleCloseModal}
                >
                  <Close width={20} height={20} />
                </button>
              </div>
            </div>
            
            <div className={FarmFeaturesStyle["feature-modal-list"]}>
              {categories && categories.map(cat => {
                const catFeatures = features.filter(f => f.categoryId === cat.id);
                if (catFeatures.length === 0) return null;
                return (
                  <div key={cat.id} className={FarmFeaturesStyle["feature-modal-category"]}>
                    <div className={FarmFeaturesStyle["feature-modal-category-header"]}>
                      <span className={FarmFeaturesStyle["feature-modal-category-title"]}>{cat.name}</span>
                    </div>
                    <ul className={FarmFeaturesStyle["feature-modal-category-list"]}>
                      {catFeatures.map(f => (
                        <li key={f.id} className={FarmFeaturesStyle["feature-item"]}>
                          <span className={FarmFeaturesStyle["feature-icon"]} dangerouslySetInnerHTML={{ __html: f.iconSvg }} />
                          <span className={FarmFeaturesStyle["feature-label"]}>{f.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile slide-up modal styled like FarmOverview */}
      {showModal && isMobile && (
        <div className={FarmFeaturesStyle["slideup-overlay"]} onClick={handleCloseModal}>
          <div
            className={[
              FarmFeaturesStyle["slideup-panel"],
              closing ? FarmFeaturesStyle["slideDown"] : FarmFeaturesStyle["slideUp"]
            ].join(' ')}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onClick={e => e.stopPropagation()}
          >
            <div className={FarmFeaturesStyle["slideup-header"]}>
              <button
                className={FarmFeaturesStyle["slideup-close-btn"]}
                aria-label="Close"
                onClick={handleCloseModal}
              >
                <ChevronDown width={28} height={28} />
              </button>
              <span className={FarmFeaturesStyle["slideup-title"]}>{FARM_FEATURES_TITLE_MOBILE}</span>
            </div>
            <div className={FarmFeaturesStyle["feature-modal-list"]}>
              {categories && categories.map(cat => {
                const catFeatures = features.filter(f => f.categoryId === cat.id);
                if (catFeatures.length === 0) return null;
                return (
                  <div key={cat.id} className={FarmFeaturesStyle["feature-modal-category"]}>
                    <div className={FarmFeaturesStyle["feature-modal-category-header"]}>
                      <span className={FarmFeaturesStyle["feature-modal-category-title"]}>{cat.name}</span>
                    </div>
                    <ul className={FarmFeaturesStyle["feature-modal-category-list"]}>
                      {catFeatures.map(f => (
                        <li key={f.id} className={FarmFeaturesStyle["feature-item"]}>
                          <span className={FarmFeaturesStyle["feature-icon"]} dangerouslySetInnerHTML={{ __html: f.iconSvg }} />
                          <span className={FarmFeaturesStyle["feature-label"]}>{f.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

FarmFeatures.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      iconSvg: PropTypes.string.isRequired,
      categoryId: PropTypes.number,
    })
  ).isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
};
